/** Human-readable descriptions of engine outcomes and of programs. */

import type { CaseError, CheckReport, Limit, Outcome, Side } from '../engine/protocol';

/** A library's definitions as the source text that defines them. */
export function programText(entries: readonly { name: string; body: string }[]): string {
  return entries.map((entry) => `${entry.name} = ${entry.body}`).join('\n');
}

/** Where a side is, in words, for a message about one. */
function describeSide(side: Side): string {
  if (side === 'left') {
    return 'the left-hand side';
  }
  if (side === 'right') {
    return 'the right-hand side';
  }
  return `term ${side.other + 1}`;
}

export function describeLimit(limit: Limit): string {
  switch (limit.kind) {
    case 'steps':
      return `more than ${limit.limit.toLocaleString()} β-reductions`;
    case 'work':
      return `more than ${limit.limit.toLocaleString()} reduction operations`;
    case 'size':
      return `a term larger than ${limit.limit.toLocaleString()} nodes`;
    case 'depth':
      return `nesting deeper than ${limit.limit.toLocaleString()}`;
  }
}

function describeError(error: CaseError): string {
  switch (error.kind) {
    case 'parse':
      return `does not parse: ${error.error.message}`;
    case 'resolve':
      return describeResolve(error.error);
    case 'request':
      return `the request could not be read: ${error.message}`;
  }
}

function describeResolve(error: Record<string, unknown> & { kind: string }): string {
  switch (error.kind) {
    case 'missing_name':
      return `\`${String(error.name)}\` is not defined`;
    case 'parse':
      return `\`${String(error.name)}\` does not parse`;
    case 'free_atom':
      return `\`${String(error.definition)}\` refers to the undefined name \`${String(error.atom)}\``;
    case 'cycle': {
      const path = Array.isArray(error.path) ? (error.path as string[]).join(' → ') : '?';
      return `cyclic definitions: ${path}`;
    }
    case 'depth':
      return 'definitions nest too deeply';
    default:
      return 'could not be resolved';
  }
}

/** The verdict for one case, the way a judge names it. */
export type Verdict =
  | 'OK'
  | 'Wrong Answer'
  | 'Iteration Limit Exceeded'
  | 'Compilation Error';

export function verdictOf(outcome: Outcome): Verdict {
  switch (outcome.status) {
    case 'pass':
      return 'OK';
    case 'fail':
      return 'Wrong Answer';
    case 'limit_exceeded':
      return 'Iteration Limit Exceeded';
    case 'error':
      return 'Compilation Error';
  }
}

/** The verdict for the whole check: the first case that was not accepted. */
export function reportVerdict(report: CheckReport): Verdict {
  const rejected = report.cases.find((entry) => entry.outcome.status !== 'pass');
  return rejected === undefined ? 'OK' : verdictOf(rejected.outcome);
}

/**
 * The class a verdict's colour is set with, kept next to the words it styles.
 *
 * The classes are derived by an exhaustive match rather than by mangling the
 * text, so a reworded verdict fails to compile here instead of quietly losing
 * its colour on screen.
 */
export function verdictClassOf(verdict: Verdict): string {
  switch (verdict) {
    case 'OK':
      return 'ok';
    case 'Wrong Answer':
      return 'wrong-answer';
    case 'Iteration Limit Exceeded':
      return 'iteration-limit-exceeded';
    case 'Compilation Error':
      return 'compilation-error';
  }
}

/** The sentence under a passing case. */
function passSummary(detail: Extract<Outcome, { status: 'pass' }>['detail']): string {
  switch (detail.kind) {
    case 'normal_forms_equal':
      return 'the two sides normalise to the same term';
    case 'eta_equal':
      return 'the two sides are equal up to η';
    case 'distinct':
      return 'no two of them are the same term';
  }
}

/** The sentence under a failing case. */
function failSummary(detail: Extract<Outcome, { status: 'fail' }>['detail']): string {
  switch (detail.kind) {
    case 'not_equal':
      return 'the two sides normalise to different terms';
    case 'not_distinct':
      return 'two of them are the same term';
  }
}

/** The sentence under a case's verdict. */
export function outcomeSummary(outcome: Outcome): string {
  switch (outcome.status) {
    case 'pass':
      return passSummary(outcome.detail);
    case 'fail':
      return failSummary(outcome.detail);
    case 'limit_exceeded':
      return `${describeLimit(outcome.detail.limit)} on ${describeSide(outcome.detail.side)}`;
    case 'error':
      return describeError(outcome.detail);
  }
}
